import React, { useEffect, useState, useRef } from 'react'
import { useSpring, easings } from '@react-spring/web'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'
import { useList } from '../../Contexts/ListContext'

import { DeviceType, ListStatus, TemplateTarget } from '../../../models/enums'
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter'
import preventEvents from '../../../utils/preventEvents'

import TemplateGlobal from '../TemplateGlobal'
import Title from './Parts/Title'
import FilterLarge from './Parts/FilterLarge'
import FilterMobile from './Parts/FilterMobile'
import ChosenItem from './Parts/Filter/ChosenItem'
import ListContainer from './Parts/ListContainer'
import Suggestions from './Parts/Suggestions'
import AbsoluteLargeClosingButton from '../../Utils/SubView/AbsoluteLargeClosingButton'

const springEnterDuration = 1500
const springLeaveDuration = 2500

const List = ({ activity, onClose }): JSX.Element => {
  const dispatch = useDispatch()
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const isWidget = useSelector((state: RootState) => state.viewReducer.isWidget)
  const currentDeviceType = useSelector((state: RootState) => state.viewReducer.currentDeviceType)
  const { content, filters, selectedFilters, setContent } = useList()
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const lastListActivity = useSelector((state: RootState) => state.utilsReducer.lastListActivity)
  const listStatus = useSelector((state: RootState) => state.utilsReducer.listStatus)
  const closeList = useSelector((state: RootState) => state.utilsReducer.closeList)

  const [mobileFiltersOpened, setMobileFiltersOpened] = useState<boolean>(false)
  const [largeFilterOpenedKey, setLargeFilterOpenedKey] = useState<number | undefined>(undefined)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [spring, api] = useSpring(() => ({
    from: {
      transform: 'translateX(0%)'
    }
  }))
  const timerRef = useRef<NodeJS.Timer | null>(null)
  const divRef = useRef<HTMLDivElement>(null)
  const [abortController, setAbortController] = useState<AbortController | null>(null)

  useEffect(() => {
    closeList && handleClose()
  }, [closeList])

  const handleOpen = (): void => {
    dispatch(storeActions.utils.setListStatus(ListStatus.opened))
    api.start({
      from: {
        transform: 'translateX(-100%)'
      },
      to: {
        transform: 'translateX(0%)'
      },
      config: {
        duration: springEnterDuration,
        easing: easings.easeInBack
      }
    })
  }

  const handleClose = (): void => {
    dispatch(storeActions.utils.setListStatus(ListStatus.closing))
    api.start({
      from: {
        transform: 'translateX(0%)'
      },
      to: {
        transform: 'translateX(-100%)'
      },
      config: {
        duration: springLeaveDuration,
        easing: easings.easeOutExpo
      }
    })

    if (timerRef) {
      timerRef.current && clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindow', callback: () => dispatch(storeActions.directline.sendEvent({ name: 'Davi.CloseWindowList' })) }))
        onClose?.()
        setContent(undefined)
      }, springLeaveDuration)
    }
  }

  useEffect(() => {
    if (activity?.id !== lastListActivity) {
      setContent(activity)

      if (listStatus !== ListStatus.opened && timerRef.current) {
        clearTimeout(timerRef.current)
        handleOpen()
      }
    }

    return (): void => {
      activity?.id && dispatch(storeActions.utils.setLastListActivity(activity.id))
    }
  }, [activity])

  /**
   * On call :
   *  - scroll to the scrollRef HTML element at the top of the view
   */
  const scrollToRef = (): void => {
    if (scrollRef && scrollRef.current !== null) {
      scrollRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    }
  }

  useEffect(() => {
    if (divRef?.current && isWidget) {
      if (currentDeviceType === DeviceType.widget) {
        const ab = new AbortController()
        setAbortController(ab)
        preventEvents(null, divRef, ab.signal)
      } else {
        abortController?.abort()
        setAbortController(null)
      }
    }
  }, [divRef?.current, currentDeviceType])

  /**
   * On content change :
   *  - call scrollToRef method
   */
  useEffect(() => {
    const timer =
      content && isMobile && content.label !== 'Davi.NextPageContent'
        ? setTimeout(() => {
            scrollToRef()
          }, 100)
        : null

    return (): void => {
      timer && clearTimeout(timer)
    }
  }, [content])

  useEffect(() => {
    handleOpen()

    return (): void => {
      dispatch(storeActions.utils.setListStatus(ListStatus.closed))
      timerRef?.current && clearTimeout(timerRef.current)
    }
  }, [])

  return content ? (
    <React.Fragment>
      <TemplateGlobal onClick={handleClose} remote={true} target={TemplateTarget.list} animation={spring}>
        {isMobile && <FilterMobile setFiltersOpened={setMobileFiltersOpened} handleClose={handleClose} hasFilters={!!filters.length} />}

        <div ref={divRef} className='rf-w-full rf-px-4 large:rf-px-16 rf-overflow-y-scroll rf-scrollbar-hidden' onClick={isMobile ? (): void => {} : (): void => setLargeFilterOpenedKey(undefined)}>
          <div ref={scrollRef} />
          <div className={`${mobileFiltersOpened ? 'rf-hidden' : 'rf-flex'} rf-flex-col rf-items-start large:rf-pt-16 large:rf-h-full large-vertical:rf-pt-[20vh]`}>
            {content.text && (
              <Title className='rf-ml-2 rf-mt-8 large:rf-ml-0 large:rf-mt-0' title={capitalizeFirstLetter(content.text, locale)} numberOfResults={content.channelData?.numberOfResults || 0} />
            )}

            {!isMobile && filters.length ? <FilterLarge openedFilterKey={largeFilterOpenedKey} setOpenedFilterKey={setLargeFilterOpenedKey} /> : <React.Fragment />}

            {isMobile && (
              <div className='rf-flex rf-flex-row rf-mx-2 rf-gap-2 rf-mb-2 rf-flex-wrap'>
                {selectedFilters?.map((chosenFilter, key1) => {
                  return chosenFilter.filterItems.map((item, key2) => {
                    return (
                      <ChosenItem
                        className='rf-px-2'
                        key={`${key1}-${key2}`}
                        filterItem={item}
                        parent={{
                          id: chosenFilter.id,
                          title: chosenFilter.title
                        }}
                      />
                    )
                  })
                })}
              </div>
            )}

            <ListContainer hasFilterOrSuggestedAction={filters.length > 0 || content.suggestedActions?.actions?.length > 0} numberOfResults={content.channelData?.numberOfResults || 0} />

            {content.suggestedActions?.actions?.length ? <Suggestions className='rf-px-2 large:rf-px-0 large:rf-mb-4' suggestions={content.suggestedActions.actions} /> : <React.Fragment />}
          </div>
        </div>
      </TemplateGlobal>

      {/* Closing button */}
      <AbsoluteLargeClosingButton dashboardVisible={true} onClick={handleClose} />
    </React.Fragment>
  ) : (
    <React.Fragment />
  )
}

export default List
